import * as React from 'react';
import Modal from 'react-modal';
import AdminUpgradeFeatures from 'admin/components/AdminUpgradeFeatures';
import AdminUpgradePlanTermsModal from 'admin/components/AdminUpgradePlan/AdminUpgradePlanTermsModal';
import assertions from 'lib/assertions';
import Field from 'components/Field';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import { CardElement } from 'react-stripe-elements';
import Checkbox from 'components/Checkbox';
import Columns2 from 'components/Columns2';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
// @ts-ignore
import InputValueConstants from 'constants/InputValueConstants';
import Label from 'components/Label';
import LinkConstants from 'constants/LinkConstants';
import MailTo from 'components/MailTo';
import { OrganizationType } from 'globalTypes';
import Row from 'components/Row';
import Text from 'components/Text';
import { asterisked } from 'components/Label';
import {
  UpgradePlanData,
  UpgradePlanQuote,
} from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import AdminUpgradeStatsContainer from 'admin/components/AdminUpgradeStats';
import AdminUpgradePlanQuoteBox from './AdminUpgradePlanQuoteBox';
import ToastActions from 'actions/ToastActions';
import { baseTrackingPrefix } from 'constants/SelfServeConstants';

import './styles/AdminUpgradePlanPaymentForm.scss';

export type Props = {
  role?: string;
  requesterEmail?: string;
  orgType: OrganizationType;
  initialOrgUuid?: string;
  displayOrgStats?: boolean;
  quote: UpgradePlanQuote;
  submittingPayment: boolean;
  cardInputCompleted: boolean;
  cardError?: string;
  taxExempt: boolean;
  onAdvance: () => void;
  onNoOrgStats: () => void;
  onCardInputCompleted: () => void;
  onContactSales: () => void;
  onAcceptPlanTerms: (onSuccess: () => void) => void;
  onPersistState: (fields: UpgradePlanData, quote: UpgradePlanQuote, onSuccess: Function) => void;
  onTaxExemptChange: (taxExempt: boolean) => void;
};

type State = {
  billingFirstName?: string;
  billingLastName?: string;
  billingEmail?: string;
  useRequesterAsBillingContact?: boolean;
  adminVerificationUrl?: string;
  showTermsDialog: boolean;
};

class AdminUpgradePlanPaymentForm extends React.Component<Props, State> {
  trackingPrefix = `${baseTrackingPrefix}.payment_form`;

  state: State = {
    useRequesterAsBillingContact: true,
    showTermsDialog: false,
  };

  componentDidMount() {
    this.track('view');
  }

  track = (key: string) => {
    EventTracking.trackKey(`${this.trackingPrefix}.${key}`);
  };

  handeTaxExemptChange = (taxExempt: boolean) => {
    this.track('tax_exempt.click');
    this.props.onTaxExemptChange(taxExempt);
  };

  getAdminVerificationLabel = () => {
    const { requesterEmail, quote } = this.props;

    return (
      'To turn on paid features, provide an official website (e.g. a staff directory) ' +
      `that shows the job title of ${requesterEmail} at ${quote.organization.name}. ` +
      'If you need to verify another way, contact sales.'
    );
  };

  renderCardForm = () => {
    const { quote, onCardInputCompleted, cardError } = this.props;
    const subject = `Tax Exemption for ${quote.organization.name}`;

    return (
      <div>
        {quote.taxExempt && (
          <Text
            element="div"
            size="medium"
            className="AdminUpgradePaymentForm-featuresNotice"
            data-test="tax-exempt-banner">
            Email your tax exemption form to{' '}
            {
              <MailTo subject={subject} to={LinkConstants.BILLING_EMAIL}>
                {LinkConstants.BILLING_EMAIL}
              </MailTo>
            }{' '}
            with your name and your school/district name.
          </Text>
        )}
        <Row>
          <Label>Credit or debit card</Label>
          <Text color="graphite" size="xsmall" weight="light" lineHeight="xlarge">
            To pay through invoice, contact sales below.
          </Text>
          <Field error={cardError}>
            <div className="CreditCardInputField" data-test="credit-card-input">
              <CardElement
                onChange={(e: stripe.elements.ElementChangeResponse) => {
                  if (e.complete && !e.error) {
                    onCardInputCompleted();
                  }
                }}
                onFocus={() => this.track('credit_card_input.click')}
              />
            </div>
          </Field>
        </Row>
      </div>
    );
  };

  renderAdminVerificationForm = () => {
    return (
      <FormElement
        label={asterisked('Administrator verification')}
        labelNote={this.getAdminVerificationLabel()}
        validate={e => {
          assertions.url(e.target.value);
          assertions.notEmpty(e.target.value);
        }}
        onValidChange={e => this.setState({ adminVerificationUrl: e.target.value })}
        onInvalidChange={() => this.setState({ adminVerificationUrl: undefined })}
        element={Input}
        elementProps={{
          defaultValue: this.state.adminVerificationUrl,
          onFocus: () => this.track('admin_verification_url.click'),
        }}
        data-test="verification-form"
      />
    );
  };

  renderBillingContactDetailsForm = () => {
    return (
      <React.Fragment>
        <Columns2
          className="AdminUpgradePlanPaymentForm-content-columns"
          reset="mobile"
          data-test="billing-contact-form">
          <FormElement
            label="First name"
            validate={e => {
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.FIRST_NAME_LENGTH
              );
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e => this.setState({ billingFirstName: e.target.value })}
            onInvalidChange={() => this.setState({ billingFirstName: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.billingFirstName,
              onFocus: () => this.track('billing_first_name.click'),
            }}
          />
          <FormElement
            label="Last name"
            validate={e => {
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.LAST_NAME_LENGTH
              );
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e => this.setState({ billingLastName: e.target.value })}
            onInvalidChange={() => this.setState({ billingLastName: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.billingLastName,
              onFocus: () => this.track('billing_last_name.click'),
            }}
          />
        </Columns2>
        <Row>
          <FormElement
            label="Work email address"
            validate={e => {
              assertions.email(e.target.value);
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e => this.setState({ billingEmail: e.target.value })}
            onInvalidChange={() => this.setState({ billingEmail: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.billingEmail,
              onFocus: () => this.track('billing_email.click'),
            }}
          />
        </Row>
      </React.Fragment>
    );
  };

  showTermsDialog = () => {
    this.setState({ showTermsDialog: true });
  };

  hideTermsDialog = () => {
    this.setState({ showTermsDialog: false });
  };

  handleFormProgression = (purchaseSuccessful: boolean, callback: () => void) => {
    const { onPersistState, quote } = this.props;
    const upgradePlanData = {
      ...this.state,
      purchaseSuccessful,
    } as UpgradePlanData;
    onPersistState(upgradePlanData, quote, callback);
  };

  onPurchase = () => {
    const { onAcceptPlanTerms, onAdvance } = this.props;
    this.handleFormProgression(true, () => onAcceptPlanTerms(onAdvance));
  };

  onHelpRequest = () => {
    const { onContactSales } = this.props;
    this.track('contact_sales_button.click');
    this.handleFormProgression(false, () => onContactSales());
  };

  showToast(text: string, kind: 'success' | 'error') {
    if (kind === 'success') {
      ToastActions.showSuccessToast(text);
    } else {
      ToastActions.showErrorToast(text);
    }
  }

  renderProgressionButtons = () => {
    const {
      onPersistState,
      onContactSales,
      quote,
      submittingPayment,
      cardInputCompleted,
    } = this.props;

    return (
      <Buttons align="left">
        <Button
          loading={submittingPayment}
          disabled={!cardInputCompleted}
          onClick={this.showTermsDialog}
          data-test="purchase-button">
          Purchase
        </Button>
        <Button level="secondary" onClick={this.onHelpRequest} data-test="contact-sales-button">
          Contact sales
        </Button>
      </Buttons>
    );
  };

  renderSidebar = () => {
    const { displayOrgStats, initialOrgUuid, onNoOrgStats } = this.props;

    return displayOrgStats && initialOrgUuid ? (
      <AdminUpgradeStatsContainer
        organizationUuid={initialOrgUuid}
        onNoStats={onNoOrgStats}
        data-test="upgrade-stats"
      />
    ) : (
      <AdminUpgradeFeatures data-test="upgrade-features" />
    );
  };

  render() {
    const { quote, orgType, taxExempt } = this.props;
    const { useRequesterAsBillingContact, showTermsDialog } = this.state;

    return (
      <React.Fragment>
        <Modal isOpen={showTermsDialog} data-test="plan-terms-modal">
          <AdminUpgradePlanTermsModal
            organizationName={quote.organization.name}
            organizationType={orgType}
            onClose={this.hideTermsDialog}
            onSubmit={this.onPurchase}
          />
        </Modal>
        <div className="AdminUpgradePlanPaymentForm">
          <div className="AdminUpgradePlanPaymentForm-content">
            <div className="AdminUpgradePlanPaymentForm-content-body">
              <div className="AdminUpgradePlanPaymentForm-content-body-forms">
                <Row>
                  <Text weight="semibold" size="xlarge" element="div">
                    Review your Remind plan
                  </Text>
                  <Text size="medium" element="div" color="graphite">
                    Enter your payment details to complete your purchase.
                  </Text>
                </Row>
                <Row>
                  {AdminUpgradePlanQuoteBox({
                    quote,
                    taxExempt,
                    onTaxExemptChange: this.handeTaxExemptChange,
                  })}
                </Row>
                <Row>{this.renderCardForm()}</Row>
                <div>
                  <Row>
                    <Checkbox
                      checked={useRequesterAsBillingContact}
                      onChange={() =>
                        this.setState({
                          useRequesterAsBillingContact: !useRequesterAsBillingContact,
                        })
                      }
                      label="Use same name and email for billing"
                      data-test="use-requester-as-billing-contact"
                    />
                  </Row>
                  {!useRequesterAsBillingContact && this.renderBillingContactDetailsForm()}
                  <Row>{this.renderAdminVerificationForm()}</Row>
                </div>
                {this.renderProgressionButtons()}
              </div>
              <div className="AdminUpgradePlanPaymentForm-content-body--desktop-side">
                <Row size="xxxlarge">{this.renderSidebar()}</Row>
              </div>
            </div>
          </div>
          <div className="AdminUpgradePlanPaymentForm--mobile-bottom">
            <div className="AdminUpgradePlanPaymentForm-info-container">{this.renderSidebar()}</div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export { AdminUpgradePlanPaymentForm };
export default AdminUpgradePlanPaymentForm;
